import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LanguageMenu } from '@/business';

import { JourneyVisual } from './JourneyVisual';

/** DealDesk mark (same drawing as the favicon) in tokens, with the product name. */
function Wordmark() {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-2">
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" className="size-6 shrink-0">
        <rect width="32" height="32" rx="4" className="fill-primary" />
        <path d="M9 9h6.5a7 7 0 0 1 0 14H9z" fill="none" className="stroke-primary-foreground" strokeWidth={2.5} />
        <path d="M19 13h4" className="stroke-accent" strokeWidth={2.5} />
      </svg>
      <span className="text-h2 font-semibold text-ink">{t('app.name')}</span>
    </span>
  );
}

/**
 * Sign-in frame. ≥1024px: the form column at the inline start and the deal-journey visual at the inline end.
 * Below 1024px: the form alone within a narrow max width. The language control sits in the top inline-end corner;
 * before sign-in it saves the choice in this browser only.
 */
export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="page-scroll relative h-full bg-surface">
      <div className="grid min-h-full lg:grid-cols-2">
        <div className="flex min-w-0 flex-col px-6 lg:px-12">
          <header className="flex h-header shrink-0 items-center">
            <Wordmark />
          </header>
          <main className="flex flex-1 items-center justify-center py-10">
            <div className="flex w-full max-w-90 flex-col gap-6">{children}</div>
          </main>
        </div>
        <div aria-hidden="true" className="hidden min-w-0 items-center justify-center border-s border-border bg-sidebar px-12 py-16 lg:flex">
          <JourneyVisual className="max-w-160" />
        </div>
      </div>
      <LanguageMenu persist="browser" className="absolute end-4 top-2.5" />
    </div>
  );
}
